import type { ReactNode } from "react";
import clsx from "clsx";
import { formatRelativeTime } from "../util/format.js";
import styles from "./AsOfMarker.module.scss";

export interface AsOfMarkerProps {
  /** The projection's `as_of` / `last_refresh` ISO stamp. Null or unparseable renders NOTHING. */
  asOf: string | null | undefined;
  className?: string;
}

/**
 * The subtle "updated Xm ago" staleness marker, from a projection's `as_of`.
 *
 * **Why this is a primitive.** Four byte-identical copies of it existed (the dashboard's `AnalyticsBox`,
 * the Music tab, the Marketing overview, `RecentReleaseCard`) and the fourth had already drifted — the
 * release card rendered it mono at `--ac-fs-xs` while its three siblings were `--ac-fs-2xs`, so two cards
 * on the SAME dashboard page said "updated 3h ago" in different type. That is the shape the component
 * library exists to stop: a difference small enough to look deliberate and large enough to be wrong.
 *
 * Renders nothing rather than a placeholder when there is no stamp — a card that has never landed data has
 * no freshness to report, and "updated never" is worse than silence.
 */
export function AsOfMarker({ asOf, className }: AsOfMarkerProps) {
  const rel = formatRelativeTime(asOf);
  if (!rel) return null;
  return (
    <span className={clsx(styles.asOf, className)} title={asOf ?? undefined}>
      updated {rel}
    </span>
  );
}

export interface HeadRailProps {
  children: ReactNode;
  className?: string;
}

/**
 * A card head's right-hand rail: everything pinned right, STACKED and right-aligned — the summary stat or
 * action chip on top, the `AsOfMarker` beneath it (PO ruling, ART-100).
 *
 * It is a component rather than four copies of `flex-direction: column` because the ORDER is the ruling.
 * Laid out as flex SIBLINGS of the head's main column (what every one of these heads did before), a stat
 * and the as-of marker sit side by side and the head reads as three columns competing for the same corner;
 * stacked, the marker reads as a footnote to the stat above it, which is what it is.
 */
export function HeadRail({ children, className }: HeadRailProps) {
  return <div className={clsx(styles.rail, className)}>{children}</div>;
}
